import { useRef, useState, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { useURLParams } from "@/contexts/URLParamsContext";
import LandingPage from "./LandingPage";
import { useTeamMembers } from "@/hooks/team/useTeamMembers";
import ErrorDisplay from "@/components/common/error/ErrorDisplay";
import { Loader } from "lucide-react";
import FloatingAgentCard from "../components/features/team/FloatingAgentCard";
import MobileAgentCard from "../components/features/team/MobileAgentCard";
import { ResponsiveContainer } from "@/components/shared/responsive";
// Import the TeamMemberHighlights component
import TeamMemberHighlights from "../components/features/team/TeamMemberHighlights";
import { TeamMember } from "@/api/teamMembers";

const TeamMemberPage = () => {
  const { slugOrFirstName } = useParams<{ slugOrFirstName: string }>();
  const { config } = useTenant() as { config: any };
  const { updateParams } = useURLParams();

  const organizationId = config?.id || "";

  const hasSetParams = useRef(false);
  const [isMobileCardMinimized, setIsMobileCardMinimized] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const lastScrollY = useRef(0);
  const scrollListenerRef = useRef<((this: Window, ev: Event) => any) | null>(
    null
  );

  // Track if user has manually expanded the card
  const userExpandedCard = useRef(false);

  // Setup scroll listener on component mount
  useLayoutEffect(() => {
    // Define the scroll handler
    const handleScroll = (event: Event) => {
      // Only apply this effect on mobile devices
      if (window.innerWidth >= 1024) return; // 1024px is the lg breakpoint in Tailwind

      const currentScrollY = window.scrollY;

      // Set scrolled state to control z-index
      setIsScrolled(currentScrollY > 50);

      // Only auto-minimize if user hasn't manually expanded
      if (!userExpandedCard.current) {
        // Minimize the card when scrolling down past a threshold
        if (currentScrollY > lastScrollY.current && currentScrollY > 50) {
          setIsMobileCardMinimized(true);
        }
        // Allow the card to be opened when scrolling back up significantly
        else if (
          lastScrollY.current - currentScrollY > 30 &&
          currentScrollY < 100
        ) {
          setIsMobileCardMinimized(false);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    // Store the handler in a ref so we can remove it later
    scrollListenerRef.current = handleScroll;

    // Add the event listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Cleanup function to remove the event listener
    return () => {
      if (scrollListenerRef.current) {
        window.removeEventListener("scroll", scrollListenerRef.current);
      }
    };
  }, []);

  // Use React Query to fetch all team members by organization
  const {
    data: teamMembers,
    isLoading,
    isError,
  } = useTeamMembers(organizationId);

  // Handle case where teamMembers is undefined
  let teamMember: TeamMember | undefined = undefined;
  if (teamMembers && teamMembers.length > 0 && slugOrFirstName) {
    // First try to find by slug (if it exists in the data)
    teamMember = teamMembers.find((member) => member.slug === slugOrFirstName);

    // If no match by slug, fall back to firstName (backward compatibility)
    if (!teamMember) {
      const normalizedName = slugOrFirstName.toLowerCase().trim();
      teamMember = teamMembers.find(
        (member) => member.first_name.toLowerCase().trim() === normalizedName
      );
    }

    console.log("Found team member:", teamMember);
  } else {
    console.log("No team members available to search through");
  }

  const teamMemberExists = !!teamMember;

  // Add agent parameter with team member email to persist across navigation
  if (teamMember && !hasSetParams.current) {
    updateParams({ agent: teamMember.email });
    hasSetParams.current = true;
  }

  const toggleMobileCard = () => {
    // Always allow toggling regardless of scroll position
    const newState = !isMobileCardMinimized;
    setIsMobileCardMinimized(newState);

    // When newState is false, it means the card is expanded (not minimized)
    // When newState is true, it means the card is minimized

    // If the user is expanding the card, prevent auto-minimize
    if (!newState) {
      userExpandedCard.current = true;
    } else {
      // If the user is minimizing the card, allow auto-behavior again
      userExpandedCard.current = false;
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex justify-center items-center"
        style={{ minHeight: "calc(100vh - 100px)" }}
      >
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorDisplay
        message="Error Loading Team Members"
        details="There was an error loading the team members. Please try again later."
        buttonText="Go to Home Page"
        onButtonClick={() => (window.location.href = "/home")}
      />
    );
  }

  if (!teamMemberExists && !isLoading) {
    return (
      <ErrorDisplay
        message="Team Member Not Found"
        details={`The team member "${slugOrFirstName}" doesn't exist or couldn't be found.`}
        buttonText="Go to Home Page"
        onButtonClick={() => (window.location.href = "/home")}
      />
    );
  }

  const primaryColor = config?.branding?.colors?.primary || "#2563eb";

  return (
    <div className="relative">
      {/* Floating Agent Card for Desktop */}
      {teamMember && (
        <FloatingAgentCard
          teamMember={teamMember}
          primaryColor={primaryColor}
        />
      )}

      {/* Mobile Agent Card - Sticky at the top with dynamic z-index based on scroll */}
      {teamMember && (
        <div
          className={`lg:hidden sticky top-0 shadow-sm ${
            isScrolled ? "z-30" : "z-10"
          }`}
        >
          <MobileAgentCard
            teamMember={teamMember}
            primaryColor={primaryColor}
            isMinimized={isMobileCardMinimized}
            onToggleMinimize={toggleMobileCard}
          />
        </div>
      )}

      {/* Main Content with Team Member passed as prop */}
      <LandingPage teamMember={teamMember} />
    </div>
  );
};

export default TeamMemberPage;
